import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { formatToTimeZone, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';

import { ActivityLogCandidateRsvpDeclined_ActivityLogFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogContent from 'src/entities/ActivityLog/Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogCandidateRsvpDeclined_ActivityLogFragment;
}

const ActivityLogTaskCandidateRsvpDeclined: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogCandidateRsvpDeclinedDetails') {
    throw new Error('ActivityLogCandidateRsvpDeclined: details is not ActivityLogCandidateRsvpDeclinedDetails');
  }

  const { startAt, endAt, comment } = details;
  const actionLabel = useMemo(() => {
    const timezone = getLocalTimezone();

    const startDate = formatToTimeZone(startAt, 'MMM d, yyyy', timezone);
    const startTime = formatToTimeZone(startAt, 'h:mm a', timezone);
    const endTime = formatToTimeZone(endAt, 'h:mm a', timezone);

    const timezoneAbbr = getTZAbbr(timezone);

    return {
      key: 'hiring-dashboard-candidates:candidates.activityLog.declinedInterviewOn',
      fallback: `declined their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
      values: {
        startDate,
        startTime,
        endTime,
        timezone: timezoneAbbr,
      },
    };
  }, [endAt, startAt]);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      responseStatus={ResponseStatus.Declined}
      actionLabel={actionLabel.fallback}
      actionLabelKey={actionLabel.key}
      actionLabelValues={actionLabel.values}
    >
      {comment ? <ActivityLogContent content={comment} /> : null}
    </ActivityLogBase>
  );
};

ActivityLogTaskCandidateRsvpDeclined.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogCandidateRsvpDeclined_activityLog on ActivityLog {
      id
      details {
        __typename
        ... on ActivityLogCandidateRsvpDeclinedDetails {
          __typename
          startAt
          endAt
          comment
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskCandidateRsvpDeclined;
